import React, { useCallback, useEffect, useRef, useState } from "react";
import API from "common-ui-components/components/utils/API";
import responseHandler from "app/manage/software-repository/utils/responseHandler";
import scanningIcon from "common-ui-components/components/icons/scanning.svg";
import closeIcon from "common-ui-components/components/icons/close.svg";
import styles from "./PostPatchScanNotifier.module.css"

const ANIMATION_DURATION = 300;
const OPEN_DELAY = 700;

const PostPatchScanNotifier = ({ lastScanKey, setClosed = null }) => {
  const [summaryData, setSummaryData] = useState(null);
  const [isFetching, setIsFetching] = useState(false);

  const [show, setShow] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const unmountTimerRef = useRef(null);
  const openTimerRef = useRef(null);

  // 1. Centralized Fetch Logic
  const fetchScanSummary = useCallback(async (isSilentRefresh = false) => {
    if (sessionStorage.getItem("closePatchScanSummaryBanner")) {
      if (!isSilentRefresh) setShow(false);
      return;
    }

    // Only collapse the banner if a NEW scan was initiated
    if (!isSilentRefresh) {
      setIsOpen(false);
    }
    
    setIsFetching(true);

    try {
      const response = await API.get("/patch/scan-summary");
      const formattedResponse = responseHandler(response);

      if (!formattedResponse.ok) throw formattedResponse;

      const data = formattedResponse.body || {};
      const summary = {
        success: Number(data.success_count) || 0,
        inProgress: Number(data.in_progress_count) || 0,
        failed: Number(data.failed_count) || 0,
        timedOut: Number(data.timed_out_count) || 0,
      };

      const totalDevices =
        summary.success +
        summary.inProgress +
        summary.failed +
        summary.timedOut;

      if (totalDevices > 0) {
        setSummaryData(summary);

        // Only run the opening animation if a NEW scan was initiated
        if (!isSilentRefresh) {
          setShow(true);
          if (openTimerRef.current) clearTimeout(openTimerRef.current);
          openTimerRef.current = setTimeout(() => setIsOpen(true), OPEN_DELAY);
        }
      } else {
        if (!isSilentRefresh) setShow(false);
      }
    } catch (error) {
      console.error("Failed to fetch patch scan summary:", error);
      if (!isSilentRefresh) setShow(false);
    } finally {
      setIsFetching(false);
    }
  }, []);

  // 2. Trigger on Mount or parent `lastScanKey` change (Full Animation)
  useEffect(() => {
    fetchScanSummary(false);
  }, [lastScanKey, fetchScanSummary]);

  // 3. Manual Close Handler
  const handleClose = () => {
    sessionStorage.setItem("closePatchScanSummaryBanner", "true");
    setIsOpen(false);

    unmountTimerRef.current = setTimeout(() => {
      setShow(false);
      if (setClosed) setClosed(true);
    }, ANIMATION_DURATION);
  };

  // 4. Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (unmountTimerRef.current) clearTimeout(unmountTimerRef.current);
      if (openTimerRef.current) clearTimeout(openTimerRef.current);
    };
  }, []);

  // Safe exit
  if (!summaryData || !show) return null;

  // 5. Build dynamic active statuses array
  const activeStatuses = [];
  if (summaryData.inProgress > 0) {
    activeStatuses.push(
      <span key="prog" style={{ color: "#2F7EE9" }}>
        In Progress: {summaryData.inProgress}
      </span>,
    );
  }
  if (summaryData.success > 0) {
    activeStatuses.push(
      <span key="succ" style={{ color: "#2E7D32" }}>
        Success: {summaryData.success}
      </span>,
    );
  }
  if (summaryData.failed > 0) {
    activeStatuses.push(
      <span key="fail" style={{ color: "#C62828" }}>
        Failed: {summaryData.failed}
      </span>,
    );
  }
  if (summaryData.timedOut > 0) {
    activeStatuses.push(
      <span key="time" style={{ color: "#D4A017" }}>
        Timed Out: {summaryData.timedOut}
      </span>,
    );
  }

  // 6. Handle manual refresh click
  const handleRefreshClick = () => {
    if (!isFetching) {
      // Pass 'true' so the banner updates silently without closing/opening
      fetchScanSummary(true); 
    }
  };

  return (
    <div
      style={{
        maxHeight: isOpen ? "220px" : "0px",
        opacity: isOpen ? 1 : 0,
        transform: isOpen ? "translateY(0)" : "translateY(-6px)",
        overflow: "hidden",
        transition: `all ${ANIMATION_DURATION}ms ease`,
      }}
    >
      <div
        style={{
          background: "#FFFDEF",
          padding: "1rem",
          boxShadow: "0px 0px 4px rgba(0, 0, 0, 0.1)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "1rem",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "0.35rem" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            
            {/* Refresh Icon (Only shows if inProgress > 0) */}
            {summaryData.inProgress > 0 && (
              <img
                src={scanningIcon}
                className={isFetching ? styles.rotate : ""}
                width={16}
                height={16}
                alt="Refresh scan status"
                title="Refresh scan status"
                onClick={handleRefreshClick}
                style={{
                  cursor: isFetching ? "wait" : "pointer",
                }}
              />
            )}

            {/* Dynamic Header Text */}
            <span>
              <strong>
                {isFetching 
                  ? "Refreshing scan summary..." 
                  : summaryData.inProgress > 0 
                    ? "Last Scan Status :"
                    : "Last Scan Summary :"}
              </strong>
            </span>

            {/* Comma Separated Statuses - HIDDEN DURING FETCH */}
            {!isFetching && (
              <div style={{ display: "flex", fontWeight: 600, flexWrap: "wrap" }}>
                {activeStatuses.map((statusNode, index) => (
                  <React.Fragment key={index}>
                    {statusNode}
                    {index < activeStatuses.length - 1 && (
                      <span style={{ color: "#1a1a1a", marginRight: "0.4rem" }}>
                        ,
                      </span>
                    )}
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>
        </div>

        <button
          onClick={handleClose}
          aria-label="Dismiss scan notification"
          style={{
            background: "transparent",
            border: "none",
            cursor: "pointer",
            padding: "4px",
            display: "flex",
            alignItems: "center",
          }}
        >
          <img src={closeIcon} width={15} alt="" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
};

export default PostPatchScanNotifier;